/**
 * Pricing content.
 *
 * The live catalogue is served by the stripe service (GET /api/v1/catalog, written in
 * decast-stripe-service/src/config/catalog.ts), which is also what Stripe charges. What
 * follows is the bundled fallback: the page renders from it while the call is in flight,
 * and keeps it if the service cannot be reached. Prices here can therefore be stale, so
 * treat catalog.ts as the source when changing anything.
 *
 * Yearly is exactly two months free: monthly x 10.
 */
export const APP_SIGNUP = '/v2signup';
export const CONTACT = 'learn@lerenlabs.com';

/**
 * Line icons (24x24, stroked), mirrored from the icons decast.live uses on this page.
 */
export const ICONS = {
  shield:
    '<path d="M12 3l7 2.6v5.1c0 4.3-3 8.2-7 9.3-4-1.1-7-5-7-9.3V5.6L12 3z"/><path d="M8.8 11.7l2.2 2.2 4.2-4.6"/>',
  lock: '<rect x="5" y="10.5" width="14" height="9.5" rx="2"/><path d="M8.5 10.5V8a3.5 3.5 0 017 0v2.5"/>',
  users:
    '<circle cx="9" cy="8.5" r="3.2"/><path d="M3 19c0-3.1 2.7-4.8 6-4.8s6 1.7 6 4.8"/><path d="M16.2 5.6a3 3 0 010 5.8"/><path d="M18 14.4c2.2.5 3.6 1.9 3.6 4.6"/>',
  share:
    '<circle cx="18" cy="5.5" r="2.5"/><circle cx="6" cy="12" r="2.5"/><circle cx="18" cy="18.5" r="2.5"/><path d="M8.2 10.8l7.6-4.1M8.2 13.2l7.6 4.1"/>',
  fingerprint:
    '<path d="M12 4.5a7.5 7.5 0 00-7.5 7.5v2"/><path d="M19.5 14v-2A7.5 7.5 0 0016 5.3"/><path d="M8.5 12a3.5 3.5 0 017 0v3.5"/><path d="M12 12v6"/><path d="M15.5 17.5v1.5"/><path d="M8.5 15.5V18"/>',
  box: '<path d="M12 3.5l7.5 4v9l-7.5 4-7.5-4v-9z"/><path d="M4.5 7.5L12 11.5l7.5-4"/><path d="M12 11.5V20"/>',
  sparkles:
    '<path d="M11 3.5l1.7 4.3 4.3 1.7-4.3 1.7L11 15.5l-1.7-4.3L5 9.5l4.3-1.7z"/><path d="M18 14.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z"/>',
  badge:
    '<path d="M12 3.2l2.1 1.5 2.5-.2.6 2.5 2 1.6-1.2 2.3 1.2 2.3-2 1.6-.6 2.5-2.5-.2L12 18.6l-2.1-1.5-2.5.2-.6-2.5-2-1.6L6 10.9 4.8 8.6l2-1.6.6-2.5 2.5.2z"/><path d="M9.3 11.2l1.9 1.9 3.5-3.8"/>',
  database:
    '<ellipse cx="12" cy="6.5" rx="7" ry="2.8"/><path d="M5 6.5v11c0 1.6 3.1 2.8 7 2.8s7-1.2 7-2.8v-11"/><path d="M5 12c0 1.6 3.1 2.8 7 2.8s7-1.2 7-2.8"/>',
  clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 1.8"/>',
  layers:
    '<path d="M12 3.5l8 4-8 4-8-4z"/><path d="M4 12l8 4 8-4"/><path d="M4 16.5l8 4 8-4"/>',
};

export const PLANS = [
  {
    key: 'secure',
    name: 'Secure',
    audience: 'For individuals',
    layer: 'Information security',
    icon: 'shield',
    monthly: 0,
    blurb: 'Secure communication.',
    features: [
      '10 GB secure storage',
      '1 AI video hour / month',
      'Multi-channel casting included',
      'Up to 100 interactive participants',
    ],
    cta: 'Start free',
  },
  {
    key: 'sensitive',
    name: 'Sensitive',
    audience: 'For professionals',
    layer: 'Identity protection + information security',
    icon: 'lock',
    monthly: 49,
    blurb: 'Protected identities. Sensitive work.',
    features: [
      '50 GB secure storage',
      '10 AI video hours / month',
      '1 Personal DID',
      'Private & protected media',
      'Multi-channel casting included',
      'Up to 100 interactive participants',
    ],
    cta: 'Subscribe',
    popular: true,
  },
  {
    key: 'structured',
    name: 'Structured',
    audience: 'For teams & organisations',
    layer:
      'Identity protection + information management + media integrity + responsible intelligence',
    icon: 'users',
    monthly: 249,
    blurb: 'Trusted media becomes organisational knowledge.',
    features: [
      '1 TB secure storage',
      '50 AI video hours / month',
      'Organization DID',
      'Persistent organizational memory',
      '50 Trusted Credentials included',
      'Managed identities & access',
      'Additional credentials: €2.50 each',
      { label: 'Organizational Knowledge Graph', beta: true },
      { label: 'Portable containers', beta: true },
      { label: 'On-chain and Distributed Storage', beta: true },
    ],
    cta: 'Subscribe',
  },
  {
    key: 'sovereign',
    name: 'Sovereign',
    audience: 'Enterprise · for enterprises, institutions & regulated environments',
    layer: 'Identity + information + integrity + intelligence + infrastructure control',
    icon: 'layers',
    monthly: 0,
    priceLabel: 'Custom quote',
    layout: 'band',
    blurb: 'Your infrastructure. Your policies. Your intelligence.',
    footnote:
      'Dedicated deployment · identity architecture · controlled storage · provenance · ' +
      'private intelligence · integration with the Leren Trusted Intelligence Stack',
    features: [],
    cta: 'Custom quote',
  },
];

export const TOPUPS = [
  { label: '+1 Trusted Credential', price: '€2.50', icon: 'badge' },
  { label: '+100 GB Storage', price: '€10 / month', icon: 'database' },
  { label: '+10 Video Intelligence Hour Credits', price: '€20', icon: 'clock' },
];

export const LAYERS = [
  {
    name: 'Information (Security)',
    icon: 'share',
    text: 'Control how people join, share, view and interact across live and recorded media.',
  },
  {
    name: 'Identity (Protected DIDs)',
    icon: 'fingerprint',
    text: 'Personal and organizational decentralized identities control who can enter, speak, and prove participation.',
  },
  {
    name: 'Integrity (Data and Storage)',
    icon: 'box',
    text: 'Media stored with clear provenance and tamper-evident records beyond capture.',
  },
  {
    name: 'Intelligence (Responsible AI)',
    icon: 'sparkles',
    text: 'Transcripts, chapters, summaries and searchable knowledge long after the event ends.',
  },
];

export const AUDIENCES = [
  'Journalists',
  'Field reporters',
  'Researchers',
  'Educators',
  'Legal teams',
  'Organizations',
];

export const PROMISES = [
  'End-to-end security from access to archive.',
  "Know who captured it. Know it's real.",
  'Turn content into insight, instantly.',
  'Preserve with proof. Recall with confidence.',
];

export const FAQS = [
  {
    q: 'What counts as an AI video hour?',
    a: 'One hour of recorded media processed for transcription, chapters, summaries and knowledge-graph indexing. Plan hours reset monthly; top-up hours do not expire within the billing year.',
  },
  {
    q: 'Can I go beyond 100 interactive participants?',
    a: 'Yes. Beyond 100 interactive participants Decast supports much larger audiences in view-only mode, with the same provenance and access controls.',
  },
  {
    q: 'What is a Trusted Credential?',
    a: 'A verifiable credential issued by your organization DID to a person or device, for example a press credential attesting a reporter belongs to your newsroom. Structured includes 50; extra credentials are €2.50 each.',
  },
  {
    q: 'Do I need a wallet to subscribe?',
    a: 'No. You can subscribe with email and card. A DID wallet is created for you on the Sensitive, Structured and Sovereign plans so you can sign, prove and selectively disclose.',
  },
  {
    q: 'Can I change or cancel my plan?',
    a: 'Plans are self-service. Upgrade, downgrade or cancel at any time. Changes are prorated and your stored media stays accessible on the free Secure tier.',
  },
];

export const YEARLY_MONTHS = 10;

export const yearlyOf = (monthly, months = YEARLY_MONTHS) => monthly * months;

/**
 * Shape the served catalogue into what this page renders. Returns null when the payload is
 * not usable, which leaves the bundled content in place.
 */
export function fromCatalog(c) {
  if (!c || !Array.isArray(c.tiers) || !c.tiers.length) return null;
  return {
    plans: c.tiers.map((t) => ({
      key: t.tier,
      name: t.name,
      audience: t.audience || '',
      layer: t.layer,
      icon: t.icon,
      monthly: t.monthly,
      // a tier with nothing to buy says so instead of showing a price
      priceLabel: t.priceLabel || '',
      layout: t.layout || 'card',
      footnote: t.footnote || '',
      blurb: t.blurb,
      features: t.features || [],
      cta: t.cta,
      popular: !!t.popular,
      // { month: { planId, available, ... }, year: {...} }
      planRefs: t.plans || {},
    })),
    topups: c.topups && c.topups.length ? c.topups : TOPUPS,
    layers: c.layers && c.layers.length ? c.layers : LAYERS,
    audiences: c.audiences && c.audiences.length ? c.audiences : AUDIENCES,
    promises: c.promises && c.promises.length ? c.promises : PROMISES,
    faqs: c.faqs && c.faqs.length ? c.faqs : FAQS,
    yearlyMonths: c.yearlyMonths || YEARLY_MONTHS,
  };
}
